import { useState, useEffect, useRef } from "react";
import { useGameAuth } from "@/hooks/useGameAuth";
import { GameLayout } from "@/components/game/GameLayout";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Navigate } from "react-router-dom";
import { Trophy, Medal, TrendingUp, Crown, ChevronLeft, ChevronRight } from "lucide-react";
import cyberBg from "@/assets/game/cyber-topsellers.png";
import { getGamePlayerLabel } from "@/lib/gameDisplayName";

interface LeaderboardEntry {
  player_id: string;
  display_name: string;
  avatar_url: string | null;
  virtual_balance: number;
  portfolio_value: number;
  net_profit: number;
  nfts_owned: number;
  nfts_created: number;
  total_earned: number;
}

const PAGE_SIZE = 50;

export default function GameLeaderboard() {
  const { session, player, loading, signOut } = useGameAuth();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [activePage, setActivePage] = useState(0);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    // No participant cap — fetch all ranked players
    const { data } = await supabase
      .from("game_leaderboard")
      .select("*");

    setEntries((data as unknown as LeaderboardEntry[]) || []);
    setLoadingData(false);
  };

  if (loading) return null;
  if (!session || !player) return <Navigate to="/game" replace />;

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-400 drop-shadow-lg" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="w-6 text-center text-sm text-gray-500 font-mono">#{rank}</span>;
  };

  // Split entries into pages of 50
  const pages: LeaderboardEntry[][] = [];
  for (let i = 0; i < entries.length; i += PAGE_SIZE) {
    pages.push(entries.slice(i, i + PAGE_SIZE));
  }
  if (pages.length === 0) pages.push([]);

  const scrollToPage = (idx: number) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const target = Math.max(0, Math.min(pages.length - 1, idx));
    scroller.scrollTo({ left: target * scroller.clientWidth, behavior: "smooth" });
    setActivePage(target);
  };

  const handleScroll = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const idx = Math.round(scroller.scrollLeft / scroller.clientWidth);
    if (idx !== activePage) setActivePage(idx);
  };

  return (
    <GameLayout player={player} onSignOut={signOut}>
      <div
        className="cyber-page-bg max-w-2xl mx-auto px-4 py-8 space-y-6 relative"
        style={{ ['--cyber-bg' as string]: `url(${cyberBg})` }}
      >
        {/* Glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -z-10 w-60 h-60 bg-yellow-500/10 rounded-full blur-[100px]" />

        <div className="text-center space-y-3">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center mx-auto shadow-xl shadow-yellow-500/30">
            <Trophy className="w-8 h-8 text-black" />
          </div>
          <h1 className="text-3xl font-extrabold text-game-gradient">Leaderboard</h1>
          <p className="text-sm text-gray-400 max-w-md mx-auto">
            Top traders win <span className="text-cyan-400 font-bold">real EMON tokens</span>! Ranked by net profit.
          </p>
        </div>

        {!loadingData && entries.length > 0 && (
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={() => scrollToPage(activePage - 1)}
              disabled={activePage === 0}
              className="p-2 rounded-lg bg-black/40 border border-purple-500/30 text-purple-300 disabled:opacity-30 hover:bg-purple-500/10 transition"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none flex-1 justify-center" style={{ scrollbarWidth: "none" }}>
              {pages.map((_, idx) => {
                const start = idx * PAGE_SIZE + 1;
                const end = Math.min((idx + 1) * PAGE_SIZE, entries.length);
                const isActive = idx === activePage;
                return (
                  <button
                    key={idx}
                    onClick={() => scrollToPage(idx)}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-bold whitespace-nowrap transition ${
                      isActive
                        ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/40"
                        : "bg-black/40 border border-purple-500/20 text-gray-400 hover:text-white"
                    }`}
                  >
                    {start}–{end}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => scrollToPage(activePage + 1)}
              disabled={activePage >= pages.length - 1}
              className="p-2 rounded-lg bg-black/40 border border-purple-500/30 text-purple-300 disabled:opacity-30 hover:bg-purple-500/10 transition"
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {loadingData ? (
          <Card className="card-game-neon shadow-xl">
            <CardContent className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-purple-400 border-t-transparent" />
            </CardContent>
          </Card>
        ) : entries.length === 0 ? (
          <Card className="card-game-neon shadow-xl">
            <CardContent>
              <p className="text-center py-12 text-gray-500">No players yet — be the first!</p>
            </CardContent>
          </Card>
        ) : (
          <div
            ref={scrollerRef}
            onScroll={handleScroll}
            className="flex overflow-x-auto snap-x snap-mandatory scrollbar-none -mx-4 px-4"
            style={{ scrollbarWidth: "none" }}
          >
            {pages.map((pageEntries, pageIdx) => (
              <div
                key={pageIdx}
                className="snap-center shrink-0 w-full pr-2"
              >
                <Card className="card-game-neon shadow-xl">
                  <CardContent className="p-0">
                    <div className="px-4 py-2 border-b border-white/5 flex items-center justify-between">
                      <span className="text-[11px] font-bold text-purple-300 uppercase tracking-wider">
                        Ranks {pageIdx * PAGE_SIZE + 1}–{Math.min((pageIdx + 1) * PAGE_SIZE, entries.length)}
                      </span>
                      <span className="text-[10px] text-gray-500">{pageEntries.length} players</span>
                    </div>
                    <div className="divide-y divide-white/5">
                      {pageEntries.map((entry, localIdx) => {
                        const rank = pageIdx * PAGE_SIZE + localIdx + 1;
                        const isMe = entry.player_id === player.id;
                        return (
                          <div
                            key={entry.player_id}
                            className={`flex items-center gap-3 px-4 py-3.5 transition-colors ${
                              isMe
                                ? "bg-purple-500/5 border-l-2 border-purple-400"
                                : rank <= 3
                                ? "bg-yellow-500/[0.03]"
                                : ""
                            }`}
                          >
                            <div className="w-8 flex justify-center">{getRankIcon(rank)}</div>
                            <div className="flex-1 min-w-0">
                              <p className={`font-bold text-sm truncate ${isMe ? "text-cyan-400" : "text-white"}`}>
                                {getGamePlayerLabel(entry.display_name)} {isMe && "(You)"}
                              </p>
                              <p className="text-xs text-gray-500">
                                {entry.nfts_owned} NFTs • ${Number(entry.virtual_balance).toFixed(2)} balance
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-extrabold text-sm text-cyan-400">
                                ${Number(entry.virtual_balance).toFixed(2)}
                              </p>
                              <p className="text-xs text-gray-500 flex items-center gap-1 justify-end">
                                <TrendingUp className="w-3 h-3" />
                                {Number(entry.net_profit) >= 0 ? "+" : ""}${Number(entry.net_profit).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        )}
      </div>
    </GameLayout>
  );
}
